import { useEffect, useCallback, FunctionComponent } from 'react';
import { Notification } from '~types';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useYaMetrica } from '~frontend/provider/yametrica';
import { useQuery, gql, useMutation, useSubscription } from '@apollo/client';
import { toast, TypeOptions } from 'react-toastify';
import { useQueue, useCookie } from 'react-use';

import { useOpenModal } from '../../components/modals/hooks';

const FRAGMENT_NOTIFICATION_FILEDS = gql`
  fragment NotificationFields on Notification {
    id
    text
    title
    type
    ttl
    props
  }
`;

const GET_NOTIFICATION = gql`
  query Notifications {
    getNotifications {
      ...NotificationFields
    }
  }

  ${FRAGMENT_NOTIFICATION_FILEDS}
`;

interface NotificationsData {
  getNotifications: Notification[];
}

interface NotificationsSubscribeData {
  pushNotification: Notification;
}

interface NotificationsMutationData {
  deleteNotification: Notification;
}

interface NotificationsMutationVars {
  id: number;
}

interface ModalTypes {
  add: any;
  remove: any;
  first: {
    id: number;
    title: string;
    props: any;
  };
}

const Notifications: FunctionComponent = () => {
  const [bloggerAuth] = useCookie('bloggerAuth');
  const yametrica = useYaMetrica();
  const { onShow } = useOpenModal();
  const { t } = useTranslation();

  const {
    add: addModal,
    remove: removeModal,
    first: activeModal,
  }: ModalTypes = useQueue();

  const { loading, error } = useQuery<NotificationsData>(GET_NOTIFICATION, {
    ssr: false,
    onCompleted: data => {
      const getNotifications = data?.getNotifications || [];

      for (const notification of getNotifications) {
        pushNotification(notification);
      }
    },
  });

  const [deleteNotification] = useMutation<
    NotificationsMutationData,
    NotificationsMutationVars
  >(
    gql`
      mutation deleteNotification($id: Int!) {
        deleteNotification(id: $id) {
          id
        }
      }
    `,
    {
      update(cache, { data: { deleteNotification } }) {
        cache.modify({
          fields: {
            getNotifications(list, { readField }) {
              return list.filter(
                (notification: any) =>
                  readField('id', notification) !== deleteNotification.id,
              );
            },
          },
        });
      },
    },
  );

  const onClose = useCallback(
    async id => {
      await deleteNotification({ variables: { id: _.toInteger(id) } });
    },
    [deleteNotification],
  );

  const pushNotification = ({
    title,
    type,
    ttl,
    text,
    id,
    props,
  }: Notification) => {
    if (_.toInteger(type) === 1) {
      if (bloggerAuth && title.includes('Promo')) {
        return false;
      }
      addModal({ title, props, id });
    } else {
      toast(
        <div id={String(id)}>
          <div
            className="notify-title"
            dangerouslySetInnerHTML={{ __html: t(title) }}
          />
          <div
            className="notify-text"
            dangerouslySetInnerHTML={{ __html: t(text, props) }}
          />
          {props?.tradeUrl && (
            <a
              className="notify-btn btn btn_medium btn_green"
              href={props?.tradeUrl}
              target="_blank"
              rel="noreferrer"
            >
              <span className="icon icon-profile"></span>
              {t('Accept')}
            </a>
          )}
        </div>,
        {
          type: type as TypeOptions,
          autoClose: ttl < 1 ? 5000 : ttl,
          onClose: () => {
            onClose(id);

            if (props?.getPayment) {
              yametrica.sendTransaction({
                id: props?.getPayment?.id,
                revenue: props?.getPayment?.revenue,
                name: `Пополнение баланса`,
                coupon: props?.getPayment?.coupon,
                price: props?.getPayment?.price,
                userId: props?.getPayment?.userId,
                paymentMethodName: props?.getPayment?.paymentMethodName,
              });
            }
          },
          //    containerId,
        },
      );
    }
  };

  useSubscription<NotificationsSubscribeData>(
    gql`
      subscription pushNotification {
        pushNotification {
          ...NotificationFields
        }
      }

      ${FRAGMENT_NOTIFICATION_FILEDS}
    `,
    {
      onData: ({ data }) => {
        const notification = data?.data?.pushNotification;

        if (notification) {
          pushNotification(notification);
        }
      },
    },
  );

  useEffect(() => {
    if (activeModal) {
      const onCloseCall = () => {
        onClose(activeModal.id);

        removeModal();
      };

      onShow(activeModal.title, {
        onClose: onCloseCall,
        ...activeModal.props,
      });
    }
  }, [activeModal]);

  if (loading) {
    return null;
  } else if (error) {
    return <>Notifications | Error! {error.message}</>;
  }

  return null;
};

export default Notifications;
